import { faBadgeCheck } from '@fortawesome/pro-regular-svg-icons/faBadgeCheck';
import { faCalendarClock } from '@fortawesome/pro-regular-svg-icons/faCalendarClock';
import { faFileCertificate } from '@fortawesome/pro-regular-svg-icons/faFileCertificate';
import { faMoneyCheckDollarPen } from '@fortawesome/pro-regular-svg-icons/faMoneyCheckDollarPen';
import { faTriangleExclamation } from '@fortawesome/pro-regular-svg-icons/faTriangleExclamation';
import { faUniversity } from '@fortawesome/pro-regular-svg-icons/faUniversity';
import { faUserClock } from '@fortawesome/pro-regular-svg-icons/faUserClock';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Avatar, Box, Dialog, DialogActions, DialogContent, FormControl, FormHelperText, FormLabel, Grid, InputAdornment, InputLabel, Link, MenuItem, Select, Skeleton, Stack, Tab, TextField, Typography } from '@mui/material';
import { AppImages } from 'app/appImages';
import { MprcBarTabs, MprcCard, MprcConfirmDialog, MprcDialogTitleClose, MprcFileSelection, MprcFormSubHeader, MprcJoinComponents, MprcRequestErrorDialog, MprcRequestIndicator, MprcText } from 'components';
import { ButtonCancel, ButtonSave } from 'components/buttons';
import { MprcDatePicker } from 'components/MprcDatePicker';
import { MprcNumberField } from 'components/MprcNumberField';
import { MprcSwitch } from 'components/MprcSwitch';
import { MprcTextFieldNumberMask } from 'components/MprcTextFieldNumberMask/MprcTextFieldNumberMask';
import { AsyncUploadResult, ConclusionFormGetDto, ConclusionFormSaveDto, ConclusionFormSettingsDto, CreateEnrollResults, EnrollStatus, TrainingExternalCourseApprovalOption, TrainingThreeStateInfo } from 'generated/CoreApi_v1';
import LibUtils, { ONE_MB } from 'lib/LibUtils';
import { useSnackbar } from 'notistack';
import { useCoreApi } from 'providers/CoreApiProvider';
import { useModalUserProfile } from 'providers/UserProfileProvider';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

//////////////////////////////////////////////////////

type TabOptions = "form" | "comments";

interface IProps {
    trainingId: string;
    classroomId: string;
    enrollmentId: string | null;
    masterEnrollmentId?: string;
    readonly?: boolean;
    onClose: (result?: CreateEnrollResults) => void;
}

interface IValidationFields {
    institutionOrProvider: boolean;
    startDate: boolean;
    endDate: boolean;
    dateRange: boolean;
    duration: boolean;
    attendancePercent: boolean;
    finalScore: boolean;
    pricePaid: boolean;
    certificate: boolean;
}

//////////////////////////////////////////////////////

export const ConclusionFormDialog = (props: IProps) => {

    const { trainingId, classroomId, enrollmentId = null, masterEnrollmentId = null, readonly = false, onClose } = props;

    const api = useCoreApi();
    const { t } = useTranslation("lms");
    const { enqueueSnackbar } = useSnackbar();
    const { showUser } = useModalUserProfile();

    const currDate = useRef<Date>(new Date());
    const formRef = useRef<HTMLDivElement>(null);

    const [loading, setLoading] = useState<boolean>(true);
    const [confirmSave, setConfirmSave] = useState<boolean>(false);
    const [saving, setSaving] = useState<boolean>(false);
    const [currentTab, setCurrentTab] = useState<TabOptions>("form");
    const [uploading, setUploading] = useState<boolean>(false);

    const [viewData, setViewData] = useState<ConclusionFormGetDto>(null!);
    const [loadDto, setLoadDto] = useState<ConclusionFormGetDto>(null!);

    const [settings, setSettings] = useState<ConclusionFormSettingsDto>({
        trainingName: "",
        institutionOrProviderOptions: [],
        institutionOrProvider: TrainingThreeStateInfo.Optional,
        coursePeriod: TrainingThreeStateInfo.Optional,
        courseDuration: TrainingThreeStateInfo.Optional,
        attendancePercent: TrainingThreeStateInfo.Optional,
        certificate: TrainingThreeStateInfo.Optional,
        finalScore: TrainingThreeStateInfo.Optional,
        finalScoreIsPercent: true,
        finalScoreMin: null,
        finalScoreMax: null,
        pricePaid: TrainingThreeStateInfo.Optional,
        approvalOption: TrainingExternalCourseApprovalOption.None,
        approver: null,
        fullManaged: false
    });

    const [viewError, setViewError] = useState<any>(null);

    const [formValidation, setFormValidation] = useState<IValidationFields>({
        institutionOrProvider: true,
        startDate: true,
        endDate: true,
        dateRange: true,
        duration: true,
        attendancePercent: true,
        certificate: true,
        finalScore: true,
        pricePaid: true
    });

    const [isFormValid, setIsFormvalid] = useState<boolean>(true);

    const [showAllTabs, setShowAllTabs] = useState<boolean>(true);

    /////////////////////////////////////////////////////////////

    ///
    const handleUploadSuccess = (uploadResult: AsyncUploadResult) => {
        if (uploadResult == null) {
            enqueueSnackbar(t("Student.ConclusionForm.UploadCertificateFailed"), { variant: "error" });
        }
        else {
            handleUpdateRecord("certificate", uploadResult.mainFile);
        }
    };

    //
    const handleUploadError = (error: any) => {
        enqueueSnackbar(error.message ?? error, { variant: "error" });
    };

    const handleUploadReset = () => {
        handleUpdateRecord("certificate", null);
    };

    const load = async () => {
        try {

            if (enrollmentId == null && masterEnrollmentId != null) {
                var result = await api.v1.enrollment.checkReuseECourseUnmanaged({ trainingId, masterEnrollmentId });
                if (result.data !== null) {

                    enqueueSnackbar(t(result.data.enrollStatus == EnrollStatus.Approved
                        ? "Registration.Training.EnrollReusedApprovedSuccessfull"
                        : "Registration.Training.EnrollReusedSuccessfull"
                    ), { variant: "success" });

                    onClose(result.data);
                    return;
                }
            }

            var response = await api.v1.enrollment.getCourseConclusionForm({ classroomId, enrollmentId });

            setLoadDto(response.data.formData);
            setViewData(response.data.formData);

            const settings = response.data.settings;
            setSettings(settings);
            setShowAllTabs(
                settings.attendancePercent != TrainingThreeStateInfo.NoShow
                || settings.certificate != TrainingThreeStateInfo.NoShow
                || settings.courseDuration != TrainingThreeStateInfo.NoShow
                || settings.coursePeriod != TrainingThreeStateInfo.NoShow
                || settings.finalScore != TrainingThreeStateInfo.NoShow
                || settings.institutionOrProvider != TrainingThreeStateInfo.NoShow
                || settings.pricePaid != TrainingThreeStateInfo.NoShow
            );
        }
        catch (e) {
            setViewError(e);
        }
        finally {
            setLoading(false);
        }
    };

    const save = async () => {
        setConfirmSave(validateForm());
    };

    const doSave = async () => {
        if (readonly) return;

        const formData: ConclusionFormSaveDto = {
            approved: viewData.approved,
            attendencePercent: viewData.attendancePercent,
            certificateFileId: viewData.certificate?.fileId ?? null,
            durationHours: viewData.durationHours,
            durationMinutes: viewData.durationMinutes,
            endDate: viewData.endDate,
            finalScore: viewData.finalScore,
            institutionOrProvider: viewData.institutionOrProvider,
            pricePaid: viewData.pricePaid,
            remarks: viewData.remarks,
            startDate: viewData.startDate
        };

        setSaving(true);

        try {
            var response = enrollmentId == null
                ? await api.v1.enrollment.registerConclusionForm({ classroomId, masterEnrollmentId, formData })
                : await api.v1.enrollment.updateConclusionForm({ enrollmentId, formData })
                ;
            if (response.data) onClose(response.data);
        }
        catch (e) {
            setViewError(e);
        }
        finally {
            setSaving(false);
        }
    };


    ///
    function handleUpdateRecord(name: keyof ConclusionFormGetDto, v: any) {
        let newViewData = { ...viewData, [name]: v };

        if (name === "durationHours") {
            if (viewData.durationMinutes == null) newViewData.durationMinutes = 0;
        }
        else if (name === "durationMinutes") {
            if (Number(v) > 59) newViewData.durationMinutes = 59;
            if (newViewData.durationHours == null) newViewData.durationHours = 0;
        }

        setViewData(newViewData);
    }

    ///
    const validateForm = () => {
        let newFormValidation = { ...formValidation };

        const duration = (Number(viewData.durationMinutes || 0) + Number(viewData.durationHours || 0));
        newFormValidation.duration = settings.courseDuration != TrainingThreeStateInfo.Required || duration > 0;

        newFormValidation.startDate = settings.coursePeriod != TrainingThreeStateInfo.Required || (viewData.startDate != null && !isNaN(viewData.startDate.getTime()));

        newFormValidation.endDate = settings.coursePeriod != TrainingThreeStateInfo.Required || (viewData.endDate != null && !isNaN(viewData.endDate.getTime()));

        newFormValidation.dateRange =
            (viewData.startDate == null && viewData.endDate == null)
            ||
            (viewData.startDate != null && viewData.endDate != null && viewData.endDate >= viewData.startDate)
            ;

        newFormValidation.attendancePercent = settings.attendancePercent != TrainingThreeStateInfo.Required || viewData.attendancePercent != null;

        newFormValidation.certificate = settings.certificate != TrainingThreeStateInfo.Required || viewData.certificate != null;

        newFormValidation.institutionOrProvider = settings.institutionOrProvider != TrainingThreeStateInfo.Required || (viewData.institutionOrProvider != null && viewData.institutionOrProvider.length > 0);

        newFormValidation.finalScore = settings.finalScore != TrainingThreeStateInfo.Required || (viewData.finalScore != null && ((settings.finalScoreIsPercent == false && viewData.finalScore >= settings.finalScoreMin! && viewData.finalScore <= settings.finalScoreMax!) || viewData.finalScore >= 0));

        const isValid = Object.values(newFormValidation).filter((x) => x == false).length == 0;

        setFormValidation(newFormValidation);
        setIsFormvalid(isValid);

        return isValid;
    };

    //////////////////////////////////////////////////////
    /// EFFECT'S
    //////////////////////////////////////////////////////

    useEffect(() => {
        load();
    }, []);


    //////////////////////////////////////////////////////
    /// RENDER
    //////////////////////////////////////////////////////

    const RenderHeader = () => {
        return settings.approver == null
            ? null
            : (
                <MprcCard paperColor="notice" elevation={0}>
                    <Box sx={{ p: .5 }}>
                        <Stack direction="row" spacing={1} alignItems="center">
                            <Link variant="inherit" onClick={() => showUser(settings.approver!.userId)}>
                                <Avatar
                                    variant="circular"
                                    src={settings.approver.thumbnailUrl ?? AppImages.UserThumbnail}
                                    sx={{ width: 36, height: 36 }}
                                />
                            </Link>

                            <Typography component="div" variant="body2">
                                <span>{t("Student.ConclusionForm.ApproverHint")}</span>
                                &nbsp;
                                <Link variant="inherit" onClick={() => showUser(settings.approver!.userId)}>
                                    {settings.approver.name ?? settings.approver.name}
                                </Link>
                            </Typography>
                        </Stack>
                    </Box>
                </MprcCard>
            );
    };

    const RenderComments = () => {
        return (
            <Stack direction="column" height="100%">
                <MprcFormSubHeader
                    title={t("Student.ConclusionForm.RemarksTitle")}
                    subtitle={t("Student.ConclusionForm.RemarksSubtitle")}
                />
                <TextField
                    autoFocus
                    multiline
                    fullWidth
                    variant="outlined"
                    disabled={readonly}
                    placeholder={t("Student.ConclusionForm.RemarksPlaceholder")}
                    value={viewData.remarks}
                    slotProps={{
                        input: {
                            inputProps: { maxLength: 512 }
                        }
                    }}
                    onChange={(e) => handleUpdateRecord("remarks", e.target.value)}
                    sx={{
                        height: "100%",
                        overflow: "auto"
                    }}
                />
            </Stack>

        );
    };

    const RenderForm = () => {
        return (
            <div id="form" ref={formRef}>
                <Grid container spacing={2} direction="column" className="focusEl">
                    {
                        settings.institutionOrProvider != TrainingThreeStateInfo.NoShow &&
                        <Grid>
                            <Stack direction="row" spacing={2} alignItems="end">
                                <FontAwesomeIcon icon={faUniversity} size="2x" />
                                {
                                    viewData == null
                                        ? <Skeleton width={200} height={24} />
                                        : settings.institutionOrProviderOptions == null || settings.institutionOrProviderOptions?.length === 0
                                            ? <TextField
                                                fullWidth
                                                disabled={readonly}
                                                label={t("Student.ConclusionForm.InstitutionOrProviderLabel")}
                                                placeholder={t("Student.ConclusionForm.InstitutionOrProviderPlaceholder")}
                                                required={settings!.institutionOrProvider == TrainingThreeStateInfo.Required}
                                                value={viewData.institutionOrProvider ?? ""}
                                                slotProps={{
                                                    input: {
                                                        inputProps: { maxLength: 255 }
                                                    }
                                                }}
                                                onChange={(e) => handleUpdateRecord("institutionOrProvider", e.target.value)}
                                                error={!formValidation.institutionOrProvider}
                                            />
                                            :
                                            <FormControl
                                                required={settings!.institutionOrProvider == TrainingThreeStateInfo.Required}
                                                fullWidth
                                                error={!formValidation.institutionOrProvider}
                                            >
                                                <InputLabel id="institutionLabel">{t("Student.ConclusionForm.InstitutionOrProviderLabel")}</InputLabel>
                                                <Select
                                                    disabled={readonly}
                                                    label={t("Student.ConclusionForm.InstitutionOrProviderLabel")}
                                                    displayEmpty
                                                    value={viewData.institutionOrProvider ?? ""}
                                                    required={settings!.institutionOrProvider == TrainingThreeStateInfo.Required}
                                                    onChange={(e) => handleUpdateRecord("institutionOrProvider", e.target.value)}
                                                    error={!formValidation.institutionOrProvider}
                                                >
                                                    {
                                                        settings.institutionOrProvider == TrainingThreeStateInfo.Optional &&
                                                        <MenuItem key={"institutionOrProvider-none"} value="">-</MenuItem>
                                                    }
                                                    {
                                                        settings?.institutionOrProviderOptions != undefined &&
                                                        settings.institutionOrProviderOptions.map((item, index) => (<MenuItem key={index} value={item}>{item}</MenuItem>))
                                                    }
                                                </Select>
                                            </FormControl>
                                }
                            </Stack>
                        </Grid>
                    }

                    {
                        settings.coursePeriod != TrainingThreeStateInfo.NoShow &&
                        <Grid>
                            <Stack direction="row" spacing={2} alignItems="end">
                                <FontAwesomeIcon icon={faCalendarClock} size="2x" />
                                <div>
                                    <Stack direction="row" spacing={2} width="100%">
                                        {
                                            viewData == null
                                                ? <Skeleton width={200} height={24} />
                                                : <>
                                                    <MprcDatePicker
                                                        disabled={readonly}
                                                        label={t("Student.ConclusionForm.StartDateLabel")}
                                                        required={settings.coursePeriod == TrainingThreeStateInfo.Required}
                                                        maxDate={currDate.current}
                                                        value={viewData.startDate || null}
                                                        onChange={e => handleUpdateRecord("startDate", e)}
                                                        error={!formValidation.startDate}
                                                        width={200}
                                                    />
                                                </>
                                        }
                                        {
                                            viewData == null
                                                ? <Skeleton width={200} height={24} />
                                                : <>
                                                    <MprcDatePicker
                                                        disabled={readonly}
                                                        label={t("Student.ConclusionForm.EndDateLabel")}
                                                        maxDate={currDate.current}
                                                        required={settings.coursePeriod == TrainingThreeStateInfo.Required}
                                                        value={viewData.endDate || null}
                                                        onChange={e => handleUpdateRecord("endDate", e)}
                                                        error={!formValidation.endDate}
                                                        width={200}
                                                    />
                                                </>
                                        }
                                    </Stack>
                                </div>
                            </Stack>
                            {
                                !formValidation.dateRange &&
                                <FormHelperText sx={{ ml: "42px" }} error>{t("Student.ConclusionForm.InvalidDateRangeHint")}</FormHelperText>
                            }
                        </Grid>
                    }

                    {
                        settings.courseDuration != TrainingThreeStateInfo.NoShow &&
                        <Grid>
                            <Stack direction="row" spacing={2} alignItems="end" className="focusEl">
                                <FontAwesomeIcon icon={faUserClock} size="2x" />
                                <Stack direction="row" justifyContent="space-between" alignItems="top" width="100%">
                                    {
                                        viewData == null
                                            ? <Skeleton width={200} height={24} />
                                            : <>
                                                <Stack direction="row" spacing={1}>
                                                    <TextField
                                                        id="durationHours"
                                                        disabled={readonly}
                                                        required={settings.courseDuration == TrainingThreeStateInfo.Required}
                                                        type="number"
                                                        sx={{ width: 120 }}
                                                        label={t("Student.ConclusionForm.DurationLabel")}
                                                        slotProps={{
                                                            input: {
                                                                inputProps: { min: 0 },
                                                                startAdornment: <InputAdornment position="start"></InputAdornment>,
                                                                endAdornment: <InputAdornment position="end">{t("Student.ConclusionForm.DurationHoursLabel")}</InputAdornment>,
                                                            }
                                                        }}
                                                        variant="standard"
                                                        value={viewData.durationHours || 0}
                                                        onChange={e => handleUpdateRecord("durationHours", e.target.value)}
                                                        error={!formValidation.duration}
                                                    />
                                                    <TextField
                                                        id="durationMinutes"
                                                        disabled={readonly}
                                                        label={" "}
                                                        type="number"
                                                        sx={{ width: 120 }}
                                                        slotProps={{
                                                            input: {
                                                                inputProps: { min: 0, max: 59 },
                                                                startAdornment: <InputAdornment position="start"></InputAdornment>,
                                                                endAdornment: <InputAdornment position="end">{t("Student.ConclusionForm.DurationMinutesLabel")}</InputAdornment>,
                                                            }
                                                        }}
                                                        value={viewData.durationMinutes || 0}
                                                        variant="standard"
                                                        onChange={e => handleUpdateRecord("durationMinutes", e.target.value)}
                                                        error={!formValidation.duration}
                                                    />
                                                </Stack>
                                            </>
                                    }

                                    {
                                        viewData == null
                                            ? <Skeleton width={200} height={24} />
                                            : <>
                                                <MprcNumberField
                                                    disabled={readonly}
                                                    label={t("Student.ConclusionForm.AttendanceLabel")}
                                                    min={0}
                                                    max={100}
                                                    decimals={0}
                                                    allowNegative={false}
                                                    required={settings.attendancePercent == TrainingThreeStateInfo.Required}
                                                    value={viewData.attendancePercent ?? undefined}
                                                    onChange={e => handleUpdateRecord("attendancePercent", e)}
                                                    slotProps={{
                                                        input: {
                                                            endAdornment: <InputAdornment position="end">%</InputAdornment>,
                                                        }
                                                    }}
                                                    sx={{ width: 120 }}
                                                    error={!formValidation.attendancePercent}
                                                />
                                            </>
                                    }
                                </Stack>
                            </Stack>
                        </Grid>
                    }

                    {
                        (settings.finalScore != TrainingThreeStateInfo.NoShow || settings.fullManaged) &&
                        <Grid>
                            <Stack direction="row" spacing={2} alignItems="end">
                                <FontAwesomeIcon icon={faBadgeCheck} size="2x" />
                                <div>
                                    <Stack direction="row" spacing={4} className="focusEl" alignItems="center">
                                        {
                                            viewData == null
                                                ? <Skeleton width={120} height={24} />
                                                : (
                                                    <>
                                                        {
                                                            settings.finalScore != TrainingThreeStateInfo.NoShow
                                                                ?
                                                                <MprcNumberField
                                                                    disabled={readonly}
                                                                    label={t("Student.ConclusionForm.FinalScoreLabel")}
                                                                    min={settings.finalScoreMin || 0}
                                                                    max={settings.finalScoreMax || 100}
                                                                    decimals={0}
                                                                    allowNegative={false}
                                                                    required={settings.finalScore == TrainingThreeStateInfo.Required}
                                                                    value={viewData.finalScore}
                                                                    onChange={e => handleUpdateRecord("finalScore", e)}
                                                                    slotProps={{
                                                                        input: {
                                                                            endAdornment: settings.finalScoreIsPercent
                                                                                ? <InputAdornment position="end">%</InputAdornment>
                                                                                : undefined,
                                                                        }
                                                                    }}
                                                                    sx={{ width: 120 }}
                                                                    error={!formValidation.finalScore}
                                                                />
                                                                : null
                                                        }

                                                        {
                                                            settings.fullManaged &&
                                                            <MprcSwitch
                                                                disabled={readonly}
                                                                label={t("Student.ConclusionForm.ApprovedLabel")}
                                                                checked={viewData.approved || false}
                                                                onChange={(e) => handleUpdateRecord("approved", e.target.checked)}
                                                            />
                                                        }
                                                    </>
                                                )
                                        }
                                    </Stack>


                                </div>
                            </Stack>
                            <Box sx={{ ml: "42px" }}>
                                {
                                    settings.fullManaged &&
                                    <FormHelperText sx={{ mb: 1 }}>{t("Student.ConclusionForm.ApprovedHint")}</FormHelperText>
                                }
                                {
                                    settings.finalScore != TrainingThreeStateInfo.NoShow
                                    && <FormHelperText>
                                        <MprcJoinComponents components={
                                            settings.finalScoreIsPercent
                                                ? t("Student.ConclusionForm.FinalScorePercentHint").split('\n')
                                                : [t("Student.ConclusionForm.FinalScoreLimitText", { min: settings.finalScoreMin, max: settings.finalScoreMax })]}
                                                separator={<br />} />
                                    </FormHelperText>
                                }
                            </Box>
                        </Grid>
                    }

                    {
                        settings.pricePaid != TrainingThreeStateInfo.NoShow &&
                        <Grid>
                            <Stack direction="row" spacing={2} alignItems="end" className="focusEl">
                                <FontAwesomeIcon icon={faMoneyCheckDollarPen} size="2x" />
                                {
                                    viewData == null
                                        ? <Skeleton width={200} height={24} />
                                        : <>
                                            <MprcTextFieldNumberMask
                                                isCurrency
                                                min={0}
                                                disabled={readonly}
                                                label={t("Student.ConclusionForm.PriceLabel")}
                                                required={settings.pricePaid == TrainingThreeStateInfo.Required}
                                                value={viewData.pricePaid ?? 0}
                                                onChange={value => handleUpdateRecord("pricePaid", value)}
                                                error={!formValidation.pricePaid}
                                                sx={{ width: 120 }}
                                            />
                                        </>
                                }
                            </Stack>
                        </Grid>
                    }

                    {
                        settings.certificate != TrainingThreeStateInfo.NoShow &&
                        <Grid>
                            <Stack direction="row" spacing={2} alignItems="top" mt={2}>
                                <FontAwesomeIcon icon={faFileCertificate} size="2x" />
                                <div>
                                    <FormLabel required={settings.certificate == TrainingThreeStateInfo.Required}>
                                        {t("Student.ConclusionForm.CertificateLabel")}
                                    </FormLabel>

                                    <MprcFileSelection
                                        disabled={readonly}
                                        maxSize={5 * ONE_MB}
                                        error={!formValidation.certificate}
                                        selectedFile={viewData.certificate}
                                        onFileUploadStarted={() => setUploading(true)}
                                        onFileUploadFinished={() => setUploading(false)}
                                        onFileUploadError={() => handleUploadError}
                                        onFileUploadSuccess={handleUploadSuccess}
                                        onClear={() => handleUploadReset()}
                                        uploadMessage={[
                                            t("Student.EnrollmentView.DeliveryActivityDrawer.MessageL1"),
                                            t("Student.EnrollmentView.DeliveryActivityDrawer.MessageL2"),
                                            t("Student.EnrollmentView.DeliveryActivityDrawer.MessageL3"),
                                        ]}
                                    />
                                </div>
                            </Stack>
                        </Grid>
                    }
                </Grid >
            </div>
        );
    };

    useEffect(() => {
        // Posiciona no primeiro input disponível.
        if (formRef.current) {
            const f = formRef.current.getElementsByClassName('focusEl');
            if (f.length > 0) {
                const f0 = f[0];
                const input = f0.querySelector("input");
                const select = f0.querySelector("select");
                const textarea = f0.querySelector("textarea");
                (input ?? select ?? textarea)?.focus();
            }
        }
    }, [currentTab, loading]);

    return (
        <Dialog open maxWidth="sm" fullWidth>
            <MprcDialogTitleClose
                title={t("Student.ConclusionForm.Title")}
                subtitle={""}
                shrinkPadding
                onClose={() => onClose()}
            />
            <DialogContent sx={{ minHeight: "500px" }}>

                <MprcRequestIndicator visible={loading || saving} type={loading ? "request" : "post"} />

                <MprcRequestErrorDialog error={viewError} onClose={() => setViewError(null)} />

                {
                    viewData &&
                    <Stack direction="column" width="100%" height="100%">
                        <div>
                            {RenderHeader()}
                        </div>
                        {
                            showAllTabs
                                ? (
                                    <div>
                                        <div>
                                            <MprcBarTabs sx={{ marginBottom: 3 }}
                                                variant="fullWidth"
                                                value={currentTab}
                                                onChange={(v, selected) => setCurrentTab(selected)}>
                                                <Tab
                                                    value={"form"}
                                                    label={
                                                        <span>
                                                            {t("Student.ConclusionForm.InfoTabText")}
                                                            {!isFormValid && <MprcText color="warning"><FontAwesomeIcon icon={faTriangleExclamation} /></MprcText>}
                                                        </span>
                                                    }
                                                />
                                                <Tab
                                                    value={"comments"}
                                                    label={t("Student.ConclusionForm.CommentTabText")}
                                                />
                                            </MprcBarTabs>
                                        </div>
                                        <div>
                                            {
                                                currentTab === "form"
                                                    ? RenderForm()
                                                    : RenderComments()
                                            }
                                        </div>
                                    </div>
                                )
                                : <Box sx={{ marginTop: 3 }}>{RenderComments()}</Box>
                        }
                    </Stack>
                }

                <MprcConfirmDialog
                    open={confirmSave}
                    message={t("Student.ConclusionForm.ConfirmSaveText")}
                    onClose={
                        (result) => {
                            setConfirmSave(false);
                            if (result) doSave();
                        }
                    }
                />
            </DialogContent>
            <DialogActions>
                <ButtonCancel
                    variant="contained"
                    onClick={() => onClose()}
                />
                <ButtonSave
                    variant="contained"
                    disabled={uploading || (LibUtils.deepCompare(viewData, loadDto) && showAllTabs)}
                    onClick={() => save()}
                />
            </DialogActions>
        </Dialog>
    );
};
